<template>
  <div>
    <div id="call-records-view" class="page-table scrollable">
      <el-dialog
        width="440px"
        :visible="modalVisible"
        :before-close="handleCloseModal"
        :close-on-click-modal="false"
      >
        <h2 class="form-section-header">
          Filter Settings
        </h2>

        <h3 class="black-text mb-5">Select Date Range</h3>
        <el-date-picker
          class="mb-10"
          style="margin-left: auto;"
          v-model="historyDateRange"
          type="datetimerange"
          align="right"
          unlink-panels
          format="yyyy-MM-dd HH:mm"
          :clearable="false"
          range-separator="To"
          start-placeholder="Start date"
          end-placeholder="End date"
          :picker-options="datePickerOptions"
        >
        </el-date-picker>

        <el-form id="stats-advanced-modal" class="form-modal">
          <dads-select
            clearable
            filterable
            remote
            allow-create
            :remote-method="remoteSearchExtensions"
            placeholder="Select extension"
            v-model="extId"
          >
            <template slot="prefix">
              Extension
            </template>
            <el-option
              v-for="{ id, name, number } in extOptions"
              :key="id"
              :label="`${name} - ${number}`"
              :value="number"
            />
          </dads-select>

          <el-button class="mt-20" @click="doAdvanced">
            Update
          </el-button>
        </el-form>
      </el-dialog>

      <div id="users-stats-view" class="no-table-box page-header">
        <div class="flex justify-space-between mb-20">
          <h2 class="mb-0 mt-0 text-left">
            <v-svg sprite="message" class="voxo-orange"></v-svg>
            SMS
          </h2>
          <el-tag>
            <i class="el-icon-info" style="margin-right: 4px;"></i>
            Total: {{ total }}
          </el-tag>
        </div>

        <div class="flex pb-20">
          <div class="box">
            <el-radio-group
              v-model="presetSelection"
              @change="doPreset"
              class="hidden-xs-only"
            >
              <el-radio-button type="default" label="yesterday"
                >Yesterday</el-radio-button
              >
              <el-radio-button type="default" label="last10"
                >Last 10 Days</el-radio-button
              >
              <el-radio-button label="lastMonth">Last 30 Days</el-radio-button>
            </el-radio-group>

            <el-tooltip placement="top" :content="selectedDateRange">
              <el-button @click="modalVisible = true" class="ml-10">
                Filter
              </el-button>
            </el-tooltip>
            <el-button
              @click="resetTable"
              v-if="advancedActive"
              style="background-color: #f25a6b; color: #fff;"
            >
              Reset
            </el-button>
            <el-button class="ml-10" @click="doExport">
              Export
            </el-button>
          </div>

          <div class="box right" v-if="pages > 1">
            <el-pagination
              small
              background
              layout="prev, pager, next"
              @current-change="pageSet"
              @next-click="pageNext"
              @prev-click="pagePrevious"
              :current-page="page"
              :page-size="limit"
              :pager-count="5"
              :total="total"
            />
          </div>
        </div>

        <div class="vxe-table-height card-base card-shadow--medium">
          <vxe-grid
            round
            class="user-stats-table"
            border="inner"
            ref="xGrid"
            height="100%"
            auto-resize
            empty-text="No Data"
            :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
            :data="tableData"
            :loading="tableLoading"
            :columns="tableColumns"
          >
          </vxe-grid>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import feathersClient from "@/feathers/feathers-client";
import debounce from "lodash/debounce";
import moment from "moment-timezone";
import { ExportToCsv } from "export-to-csv";
import DadsSelect from "@/components/select";
import {phoneNumberFormatter} from "@/globals/globals";

export default {
  name: "SMSLogs",

  components: {
    DadsSelect
  },

  data() {
    return {
      tableLoading: false,
      modalVisible: false,
      tableData: [],
      presetSelection: "last10",
      advancedActive: false,
      exportCSV: false,
      page: 1,
      pages: 0,
      limit: 150,
      total: 0,

      extId: null,
      extQuery: "",
      extOptions: [],

      startDate: null,
      endDate: null,
      historyDateRange: [moment(), moment()],
      datePickerOptions: {
        shortcuts: [
          {
            text: "Last week",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment()
                  .subtract(1, "week")
                  .format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            }
          },
          {
            text: "Last month",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment()
                  .subtract(1, "month")
                  .format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            }
          },
          {
            text: "Last 3 months",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment()
                  .subtract(3, "months")
                  .format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            }
          },
          {
            text: "Last 6 months",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment()
                  .subtract(6, "months")
                  .format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            }
          },
          {
            text: "Last year",
            onClick(picker) {
              const end = moment(moment().format("YYYY-MM-DD"))
                .add(1, "day")
                .subtract(1, "minute");
              const start = moment(
                moment().subtract(12, "months").format("YYYY-MM-DD")
              );
              picker.$emit("pick", [start, end]);
            },
          },
        ],
      },

      // ⭐️ Table Columns ⭐️ //
      tableColumns: [
        {
          field: "sprite",
          title: "",
          width: 50,
          slots: {
            default: () => {
              return [
                <div class="voxo-square-icon voxo-bg-btn">
                  <v-svg class="voxo-orange" sprite="message" />
                </div>
              ];
            }
          }
        },
        {
          field: "date",
          title: "Date",
          width: "200",
          sortable: true,
          showOverflow: true,
          slots: {
            default: ({ row }) => {
              return [
                <span class="first-table-column">
                  {this.timeFormatter(row.date)}
                </span>
              ];
            }
          }
        },
        {
          field: "from",
          title: "From",
          width: "150",
          sortable: true,
          showOverflow: true,
          slots: {
            default: ({row}) => {
              return [
                <span>
                  {this.numberFormatter(row.from)}
                </span>
              ]
            }
          }
        },
        {
          field: "to",
          title: "To",
          width: "150",
          sortable: true,
          slots: {
            default: ({row}) => {
              return [
                <span>
                  {this.numberFormatter(row.to)}
                </span>
              ]
            }
          }
        },
        {
          field: "body",
          title: "Body",
          sortable: true,
          showOverflow: true
        }
      ]
    };
  },

  computed: {
    user() {
      return this.$store.state.auth.user;
    },
    userOffsetMinutes() {
      return this.$store.state.userTimeOffset;
    },
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    selectedDateRange() {
      if (this.endDate && this.startDate) {
        const dateStart = moment(this.startDate).format("M/D/YY HH:mm");
        const dateEnd = moment(this.endDate).format("M/D/YY HH:mm");
        return `${dateStart} - ${dateEnd}`;
      }
    }
  },

  watch: {
    page: debounce(function() {
      this.queryStats();
    }, 500),

    selectedTenant() {
      this.resetTable();
    },

    extQuery: debounce(function() {
      this.queryExtensions();
    }, 500)
  },

  methods: {
    queryExtensions() {
      const params = {
        query: {
          tenantId: this.selectedTenant.id,
          $limit: 25,
          $sort: { name: 1 }
        }
      };

      if (this.extQuery && this.extQuery.length) {
        const searchTerm = (this.extQuery + "")
          .replace(/[\\"']/g, "\\$&")
          .replace(/\u0000/g, "\\0");

        params.query.$or = [
          { number: { $like: `%${searchTerm}%` } },
          { name: { $like: `%${searchTerm}%` } }
        ];
      }

      feathersClient
        .service("extensions")
        .find(params)
        .then(res => {
          this.extOptions = [...res];
        });
    },

    remoteSearchExtensions(query) {
      this.extQuery = query;
    },

    pageNext() {
      if (this.page < this.pages) {
        this.page += 1;
      }
    },
    pagePrevious() {
      if (this.page > 1) {
        this.page -= 1;
      }
    },
    pageSet(page) {
      this.page = page;
    },

    handleCloseModal() {
      this.modalVisible = false;
    },

    doPreset(val) {
      if (val === "init") {
        this.endDate = moment(moment().format("YYYY-MM-DD"))
          .add(1, "day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.startDate = moment()
          .startOf("day")
          .format("YYYY-MM-DD");
        this.presetSelection = "init";
        this.updateDateRangePicker();
        this.queryStats();
      }

      if (val === "last10") {
        this.endDate = moment(moment().format("YYYY-MM-DD"))
          .add(1, "day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.startDate = moment()
          .subtract(9, "days")
          .format("YYYY-MM-DD");
        this.presetSelection = "last10";
        this.updateDateRangePicker();
        this.queryStats();
      }

      if (val === "lastMonth") {
        this.endDate = moment(moment().format("YYYY-MM-DD"))
          .add(1, "day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.startDate = moment()
          .subtract(30, "days")
          .format("YYYY-MM-DD");
        this.presetSelection = "lastMonth";
        this.updateDateRangePicker();
        this.queryStats();
      }

      if (val === "yesterday") {
        this.endDate = moment()
          .startOf("day")
          .subtract(1, "minute")
          .format("YYYY-MM-DD HH:mm");
        this.startDate = moment()
          .subtract(1, "day")
          .startOf("day")
          .format("YYYY-MM-DD HH:mm");
        this.presetSelection = "yesterday";
        this.updateDateRangePicker();
        this.queryStats();
      }
    },

    doAdvanced() {
      this.advancedActive = true;
      this.startDate = moment(this.historyDateRange[0]).format(
        "YYYY-MM-DD HH:mm:ss"
      );
      this.endDate = moment(this.historyDateRange[1]).format(
        "YYYY-MM-DD HH:mm:ss"
      );
      this.updateDateRangePicker();
      this.queryStats();
      this.handleCloseModal();
      this.presetSelection = null;
    },

    resetTable() {
      this.advancedActive = false;
      this.extId = null;
      this.exportCSV = false;
      this.page = 1;
      this.doPreset("init");
    },

    queryStats() {
      this.tableLoading = true;

      const query = {
        $limit: this.limit,
        $skip: this.page > 1 ? this.limit * (this.page - 1) : 0,
        tenantId: this.selectedTenant.id,
        date: { $gte: this.startDate, $lte: this.endDate },
        exportCSV: this.exportCSV,
        $sort: { date: -1 }
      };

      if (this.extId) {
        query.$or = [
          { to: this.extId },
          {
            from: this.extId
          }
        ];
      }

      feathersClient
        .service("sms-logs")
        .find({ query })
        .then(res => {
          if (!this.exportCSV && res && res.data) {
            this.total = res.total;
            this.tableData = res.data;
            this.pages = Math.ceil(res.total / this.limit);
            this.page = this.page > this.pages ? 1 : this.page;
          }

          if (this.exportCSV) {
            if (!res.url) {
              this.$notify({
                title: "Not Found",
                message: "File Not Found.",
                type: "warning"
              });
            } else {
              this.downloadCSV(res);
            }
          }

          this.exportCSV = false;
          this.tableLoading = false;
        })
        .catch(() => {
          this.tableData = [];
          this.tableLoading = false;
        });
    },

    updateDateRangePicker() {
      this.historyDateRange = [moment(this.startDate), moment(this.endDate)];
    },

    timeFormatterExport(value) {
      const offsetMinutes = Math.abs(this.userOffsetMinutes);

      return this.userOffsetMinutes < 0
        ? moment(value)
            .subtract(offsetMinutes, "minutes")
            .format("YY/MM/DD hh:mm:ss A")
        : moment(value)
            .add(offsetMinutes, "minutes")
            .format("YY/MM/DD hh:mm:ss A");
    },

    timeFormatter(value) {
      const offsetMinutes = Math.abs(this.userOffsetMinutes);

      return this.userOffsetMinutes < 0
        ? moment(value)
            .subtract(offsetMinutes, "minutes")
            .format("M/DD/YY h:mm A")
        : moment(value)
            .add(offsetMinutes, "minutes")
            .format("M/DD/YY h:mm A");
    },

    numberFormatter(number) {
      return phoneNumberFormatter(number);
    },

    doExport() {
      this.$confirm(`Export data for ${this.total} logs?`, {
        type: "warning"
      })
        .then(() => {
          if (this.pages === 1) {
            let csvData = [];
            for (let i = 0; i < this.tableData.length; i++) {
              const csvObj = {};
              let exclusionList = ["_XID"];
              for (let key in this.tableData[i]) {
                if (exclusionList.includes(key) === false) {
                  csvObj[key] = this.tableData[i][key]
                    ? this.tableData[i][key]
                    : "";
                }
              }
              csvData.push(csvObj);
            }

            const options = {
              filename: "sms_logs",
              showLabels: true,
              headers: ["Date", "To", "From", "Body"]
            };

            let csvRecorder = csvData.map(obj => {
              let reorderObj = {};
              reorderObj["Date"] = this.timeFormatterExport(obj.date);
              reorderObj["To"] = obj.to;
              reorderObj["From"] = obj.from;
              reorderObj["Body"] = obj.body;

              return reorderObj;
            });

            csvData.push(csvRecorder);
            const csvExporter = new ExportToCsv(options);
            csvExporter.generateCsv(csvRecorder);
          } else {
            this.exportCSV = true;
            this.queryStats();
          }
        })
        .catch(() => true);
    },

    downloadCSV(data) {
      const { url, downloadName } = data;
      const link = document.createElement("a");
      link.href = url;
      link.download = downloadName;
      link.click();
    }
  },

  mounted() {
    this.doPreset("init");
    this.queryExtensions();
  }
};
</script>
